import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { auth } from "@/lib/auth";
import { Home, Radio, Camera, Tag, Lock, LogIn, LogOut, Clapperboard, Menu, LayoutDashboard, BookOpen, Trophy, Mic2 } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import SearchBar from "@/components/SearchBar";

const Item = ({ testid, icon: Icon, label, onClick, active, highlight }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        className={`group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-300 ${
            active
                ? "text-gold"
                : highlight
                  ? "text-gold hover:bg-gold/10"
                  : "text-cream/60 hover:text-cream"
        }`}
    >
        <Icon size={15} className="shrink-0" />
        <span className="font-mono text-[9px] tracking-[0.25em]">{label}</span>
        {highlight && <span className="ml-auto h-1.5 w-1.5 animate-pulse rounded-full bg-gold" />}
    </button>
);

export default function SideDock({ page, go, authed, isAdmin }) {
    const { lang } = useLang();
    const [open, setOpen] = useState(false);
    const [userName, setUserName] = useState(null);
    useEffect(() => auth.subscribe((s) => setUserName(s.user?.name?.split(" ")[0] || null)), []);

    // navigation = aller à la page + refermer le dock (mobile : pas de mouseleave fiable)
    const nav = (id) => {
        setOpen(false);
        go(id);
    };

    return (
        <>
            {/* recherche globale — desktop en haut à gauche, flottante */}
            <div className="fixed left-5 top-5 z-[80] md:left-10">
                <SearchBar />
            </div>
            {/* thin hover edge — souris uniquement (évite le double-toggle tactile) */}
            <div
                className="fixed right-0 top-0 z-[80] h-full w-5"
                onPointerEnter={(e) => { if (e.pointerType === "mouse") setOpen(true); }}
            />
            {/* always-visible login (guest) or account (authed) */}
            <button
                data-testid="header-login-btn"
                data-cursor="LOGIN"
                onClick={() => {
                    go("portal");
                    // si déjà connecté, forcer l'affichage du formulaire pour changer de compte
                    if (authed) {
                        window.dispatchEvent(new Event("portal:show-login"));
                    }
                }}
                className="fixed right-[13.4rem] top-5 z-[80] flex items-center gap-2 border border-violet/50 bg-violet/15 px-4 py-2 font-mono text-[9px] tracking-[0.3em] text-violet backdrop-blur-md transition-all hover:bg-violet hover:text-cream md:right-[14.4rem]"
            >
                <LogIn size={11} />
                {authed ? (userName ? userName.toUpperCase() : tx(lang, T.nav.portal)) : tx(lang, T.nav.login)}
            </button>
            {/* always-visible PRIX button + MENU affordance — top right */}
            <button
                data-testid="header-pricing-btn"
                data-cursor="PRIX"
                onClick={() => {
                    go("contact");
                    // attendre le rendu de la page puis aller direct à « ESTIMEZ VOTRE PROJET »
                    setTimeout(() => scrollToId("#estimator", -70), 1300);
                    setTimeout(() => scrollToId("#estimator", -70), 2200);
                }}
                className="fixed right-[7.2rem] top-5 z-[80] border border-gold/60 bg-gold/15 px-4 py-2 font-mono text-[9px] tracking-[0.3em] text-gold backdrop-blur-md transition-all hover:bg-gold hover:text-ink md:right-[8.2rem]"
            >
                {tx(lang, T.nav.pricing)}
            </button>
            <button
                data-testid="menu-trigger"
                data-cursor="MENU"
                onPointerEnter={(e) => { if (e.pointerType === "mouse") setOpen(true); }}
                onClick={() => { if (window.matchMedia("(hover: none)").matches) setOpen((o) => !o); }}
                className="fixed right-5 top-5 z-[80] flex items-center gap-2 border border-white/15 bg-ink/60 px-4 py-2 font-mono text-[9px] tracking-[0.3em] text-cream/70 backdrop-blur-md transition-colors hover:border-gold hover:text-gold md:right-8"
            >
                <Menu size={11} />
                MENU
            </button>
                        {/* panel — compact, anchored above the GLM player, same side */}
            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="side-dock"
                        initial={{ x: 40, opacity: 0, scale: 0.96 }}
                        animate={{ x: 0, opacity: 1, scale: 1 }}
                        exit={{ x: 40, opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        onPointerLeave={(e) => { if (e.pointerType === "mouse") setOpen(false); }}
                        className="fixed bottom-[12.5rem] right-4 z-[80] border border-white/10 bg-ink/70 py-2 backdrop-blur-md md:bottom-[7rem] md:right-[5.2rem]"
                    >
                        <div className="flex items-center justify-center pb-2">
                            <Menu size={12} className="text-cream/30" />
                        </div>
                        <Item testid="dock-home" icon={Home} label={tx(lang, T.nav.home)} active={page === "home"} onClick={() => nav("home")} />
                        <Item testid="dock-videos" icon={Clapperboard} label={tx(lang, T.nav.videos)} active={page === "videos"} onClick={() => nav("videos")} />
                        <Item testid="dock-photo" icon={Camera} label={tx(lang, T.nav.photo)} active={page === "photo"} onClick={() => nav("photo")} />
                        <Item testid="dock-pricing" icon={Tag} label={tx(lang, T.nav.pricing)} active={page === "contact"} onClick={() => nav("contact")} highlight />
                        <Item testid="dock-radio" icon={Radio} label="GLM RADIO" active={page === "radio"} onClick={() => nav("radio")} />
                        <Item testid="dock-scoreboard" icon={Trophy} label={tx(lang, T.nav.scoreboard)} active={page === "culture"} onClick={() => { window.__cultureTab = "chart"; window.dispatchEvent(new CustomEvent("culture-tab", { detail: "chart" })); nav("culture"); }} />
                        <Item testid="dock-artists" icon={Mic2} label={tx(lang, T.nav.artists)} active={page === "artiste"} onClick={() => nav("artiste")} />
                        <Item testid="dock-creator" icon={Camera} label={tx(lang, T.nav.creator)} active={false} onClick={() => {
                            window.__scrollToCreator = true;
                            setOpen(false);
                            if (page !== "artiste") go("artiste");
                            else {
                                // déjà sur la page artistes → scroll direct (plusieurs passes)
                                [60, 400, 900].forEach((d) => setTimeout(() => scrollToId("#creator-6xo", -70), d));
                                setTimeout(() => { window.__scrollToCreator = false; }, 1200);
                            }
                        }} />
                        <Item testid="dock-culture" icon={BookOpen} label={tx(lang, T.nav.culture)} active={page === "culture"} onClick={() => nav("culture")} />
                        <Item testid="dock-portal" icon={LayoutDashboard} label={tx(lang, T.nav.portal)} active={page === "portal"} onClick={() => nav("portal")} />
                        <div className="mx-3 my-2 border-t border-white/10" />
                        {authed ? (
                            <>
                                {isAdmin && (
                                    <Item testid="dock-admin" icon={Lock} label="ADMIN" active={false} onClick={() => { setOpen(false); window.location.hash = "#admin"; }} />
                                )}
                                <Item testid="dock-logout" icon={LogOut} label={tx(lang, T.admin.logout)} active={false} onClick={() => auth.logout()} />
                            </>
                        ) : (
                            <Item testid="dock-login" icon={LogIn} label={tx(lang, T.nav.login)} active={false} onClick={() => nav("portal")} />
                        )}
                    </motion.nav>
                )}
            </AnimatePresence>
        </>
    );
}
